<template>
    <div
        class="pml-type-ahead"
        :class="['pml-type-ahead', { 'pml-type-ahead--disabled': isDisabled }]"
        v-pui-on-click-away="{ callback: onClickOutside }"
    >
        <input
            v-model="inputString"
            :placeholder="placeholder"
            :disabled="isDisabled"
            class="pml-type-ahead__input"
            @focusin="onInputFocusIn"
            @input="onInputChanged"
        >
        <pui-loading
            v-if="isLoading"
            :inline="true"
            :request-completed="false"
            icon-size="2.4rem"
            class="pml-type-ahead__loading-icon"
        />
        <div
            v-show="shouldShowMessageBox"
            class="pml-type-ahead__message"
        >
            {{ messageBoxText }}
        </div>
        <ul
            v-show="shouldShowResults"
            class="pml-type-ahead__results"
        >
            <li
                v-for="(option, index) in options"
                :key="index"
                :class="[
                    'pml-type-ahead__results__item',
                    {
                        'pml-type-ahead__results__item--selected': option.value === value?.value,
                    }
                ]"
                @click="onOptionClick(option)"
            >
                <div class="pml-type-ahead__results__item__label">
                    {{ option.label }}
                </div>
                <div
                    v-if="option.secondaryLabel"
                    class="pml-type-ahead__results__item__label--secondary"
                >
                    {{ option.secondaryLabel }}
                </div>
            </li>
        </ul>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Watch } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import { Debouncer } from '~/mixins/debouncer';
import { ManagedAbortControllers } from '~/mixins/managed-abort-controllers';

interface PuiSelectOption<T = string> {
    label: string;
    secondaryLabel?: string;
    value: T;
    disabled?: boolean;
    extraData?: any;
}

type SelectOption = PuiSelectOption<any>;

type RequestPromiseGenerator = (query: string) => Promise<SelectOption[]>;

@Component({})
export default class PmlTypeAhead extends mixins(Debouncer, ManagedAbortControllers) {
    @Prop({ default: () => '' })
    private placeholder!: string;

    @Prop()
    private requestPromiseGenerator?: RequestPromiseGenerator;

    @Prop({ default: () => 1000 })
    private debounceTimeout!: number;

    @Prop()
    private value?: SelectOption;

    @Prop(Boolean)
    private isDisabled!: boolean;

    private isFetchError = false;
    private isFirstOpen = true;
    private isOpen = false;
    private isLoading = false;
    private inputString = '';

    private options: SelectOption[] = [];

    private get shouldShowResults(): boolean {
        return this.isOpen && !this.isLoading && this.options.length !== 0 && !this.isFetchError;
    }

    private get shouldShowMessageBox(): boolean {
        return this.isOpen && (this.isLoading || this.options.length === 0 || this.isFetchError);
    }

    private get messageBoxText(): string {
        if (this.isLoading) {
            return 'Please wait! Loading results...'
        }

        if (this.isFetchError) {
            return 'Fetch error!'
        }

        if (this.options.length === 0) {
            return 'No results!'
        }

        return '';
    }

    @Watch('value')
    private onValueChanged(newValue: SelectOption | null): void {
        if (newValue) {
            this.inputString = newValue.label;
        }
    }

    public clearInput(): void {
        this.inputString = '';
        this.removeSelectedOption();
        this.isFirstOpen = true;
    }

    private onOptionClick(option: SelectOption): void {
        this.setSelectedOption(option);
        this.closeResults();
    }

    private onInputFocusIn(): void {
        this.openResults();
    }

    private onClickOutside(): void {
        this.closeResults();
    }

    private onInputChanged(): void {
        this.removeSelectedOption();
        this.isLoading = true;

        this.debounce(
            'fetchTypeahead',
            () => {
                this.fetchEntries();
            },
            this.debounceTimeout
        );
    }

    private setSelectedOption(option: SelectOption): void {
        this.$emit('input', option);
    }

    private removeSelectedOption(): void {
        this.$emit('input', null);
    }

    private async fetchEntries(): Promise<void> {
        if (!this.requestPromiseGenerator) {
            return;
        }

        this.isLoading = true;
        this.isFetchError = false;

        const signal = this.getSignal('fetchTypeahead', true);

        try {
            this.options = await this.requestPromiseGenerator(this.inputString);
        } catch (err) {
            if (!signal.aborted) {
                this.isFetchError = true;
            }
        } finally {
            this.isLoading = false;
            this.openResults();
        }
    }

    private openResults(): void {
        if (this.isFirstOpen) {
            this.fetchEntries().then();
            this.isFirstOpen = false;
        }

        this.isOpen = true;
    }

    private closeResults(): void {
        this.isOpen = false;
    }
}
</script>

<style scoped lang="scss">
@import 'src/globals';

.pml-type-ahead {
    $input-height: 38px;
    $input-left-padding: 12px;
    border-radius: 4px;

    border: 1px solid $warm-grey;
    background-color: $white;
    position: relative;
    display: flex;
    align-items: center;
    user-select: none;

    &:hover {
        border-color: $uniper-blue;
    }

    &--disabled {
        background-color: $pale-grey;

        &:hover {
            border-color: $warm-grey;
        }
    }

    &__input {
        padding: 0 $input-left-padding;
        line-height: 16px;
        height: $input-height;
        color: $dark-grey;
        background-color: transparent;
        border: 0;
        outline: none;
        width: 100%;
    }

    &__loading-icon {
        left: -1rem;
    }

    &__results {
        top: calc($input-height + 10px);
        border-radius: 4px;
        box-shadow: 0 1px 8px 0 rgba(0,0,0,.16);
        padding: 0;
        margin: 0;
        background-color: $white;
        width: 100%;
        position: absolute;
        z-index: 100;
        min-height: 1rem;
        max-height: 22rem;
        overflow: auto;

        &__item {
            font-size: 14px;
            padding: 10px $input-left-padding;
            line-height: 1.5;

            display: flex;
            flex-direction: column;

            &:hover {
                background-color: transparentize($uniper-blue, 0.8);
            }

            &--selected {
                background-color: transparentize($uniper-blue, 0.9);
                font-weight: bold;
            }

            &__label--secondary {
                font-size: 1.2rem;
            }
        }
    }

    &__message {
        padding: 12px $input-left-padding;
        top: calc($input-height + 10px);
        font-size: 14px;
        border-radius: 4px;
        box-shadow: 0 1px 8px 0 rgba(0,0,0,.16);
        text-align: center;
        background-color: $white;
        width: 100%;
        position: absolute;
        z-index: 1;
    }
}

.chip {
    margin: 0 4px 4px 0;
}
</style>
